import React, { useState, useEffect } from 'react';
import { Settings, Store, Users, Printer, DollarSign, Bell, Save, Upload, Image, Check } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';

const ConfiguracionPage = () => {
  const { config, updateConfig, loadConfig } = useConfig();
  const [activeTab, setActiveTab] = useState('negocio');
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({
    nombre_negocio: '',
    slogan: '',
    logo: '',
    rfc: '',
    direccion: '',
    telefono: '',
    email: '',
    moneda: 'Q',
    moneda_simbolo: 'Q',
    impresora_tickets: 'termica',
    tamano_ticket: '80mm',
    iva_incluido: true,
    usar_impuestos: false,
    porcentaje_iva: 12,
    notificaciones_stock: true,
    stock_minimo_alerta: 5
  });
  const [logoPreview, setLogoPreview] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      // Helper para convertir valores de DB a booleanos/números correctos
      const parseBool = (val) => val === '1' || val === 1 || val === true || val === 'true';
      
      setFormData({
        nombre_negocio: config.nombre_negocio || '',
        slogan: config.slogan || '',
        logo: config.logo || '',
        rfc: config.rfc || '',
        direccion: config.direccion || '',
        telefono: config.telefono || '',
        email: config.email || '',
        moneda: config.moneda || 'Q',
        moneda_simbolo: config.moneda_simbolo || 'Q',
        impresora_tickets: config.impresora_tickets || 'termica',
        tamano_ticket: config.tamano_ticket || '80mm',
        iva_incluido: config.iva_incluido !== undefined ? parseBool(config.iva_incluido) : true,
        usar_impuestos: config.usar_impuestos !== undefined ? parseBool(config.usar_impuestos) : false,
        porcentaje_iva: config.porcentaje_iva || 12,
        notificaciones_stock: config.notificaciones_stock !== undefined ? parseBool(config.notificaciones_stock) : true,
        stock_minimo_alerta: config.stock_minimo_alerta || 5
      });
      setLogoPreview(config.logo || '');
      setHasChanges(false);
    }
  }, [config]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setHasChanges(true);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona una imagen válida');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData(prev => ({
          ...prev,
          logo: reader.result
        }));
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateConfig(formData);
      if (result.success) {
        await loadConfig();
        setHasChanges(false);
        // Mostrar feedback visual sutil en lugar de alert
        const btn = document.getElementById('save-btn');
        if(btn) {
            const originalText = btn.innerText;
            btn.innerText = '¡Guardado!';
            setTimeout(() => btn.innerText = originalText, 2000);
        }
      } else {
        alert('Error al guardar: ' + (result.message || 'Error desconocido'));
      }
    } catch (error) {
      alert('Error al guardar configuración: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'negocio', name: 'Datos del Negocio', icon: Store },
    { id: 'fiscal', name: 'Impuestos y Moneda', icon: DollarSign },
    { id: 'impresion', name: 'Impresión', icon: Printer },
    { id: 'sistema', name: 'Sistema y Alertas', icon: Settings }
  ];

  return (
    <div className="pb-20"> {/* Padding bottom for sticky footer */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h1>
          <p className="text-gray-500 text-sm">Personaliza tu punto de venta</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap relative ${
                  isActive
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                {tab.name}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600" />
                )}
              </button>
            );
          })}
        </div>

        <div className="p-6 lg:p-8 min-h-[400px]">
          {activeTab === 'negocio' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                 <label className="block text-sm font-medium text-gray-700 mb-2">Logo del Negocio</label>
                 <div className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    {logoPreview ? (
                      <div className="relative group">
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          className="w-32 h-32 object-contain rounded-lg bg-white p-2 shadow-sm" 
                        />
                        <button
                          onClick={() => {
                            setLogoPreview('');
                            setFormData(prev => ({ ...prev, logo: '' }));
                            setHasChanges(true);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                          title="Eliminar logo"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center bg-white rounded-lg border border-gray-200 text-gray-400 mb-2">
                        <Image className="w-10 h-10 opacity-50" />
                      </div>
                    )}
                    
                    <label className="mt-4 cursor-pointer btn-secondary text-sm w-full flex justify-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {logoPreview ? 'Cambiar Logo' : 'Subir Logo'}
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-2 text-center">Recomendado: 200x200px (PNG/JPG)</p>
                 </div>
              </div>

              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="label">Nombre del Negocio</label>
                    <input
                      type="text"
                      name="nombre_negocio"
                      value={formData.nombre_negocio}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Ej: Abarrotes La Esperanza"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Slogan / Descripción</label>
                    <input
                      type="text"
                      name="slogan"
                      value={formData.slogan}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Ej: Los mejores precios..."
                    />
                  </div>
                  <div>
                    <label className="label">Teléfono</label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="0000-0000"
                    />
                  </div>
                  <div>
                    <label className="label">Email de Contacto</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="contacto@negocio.com"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Dirección Física</label>
                    <textarea
                      name="direccion"
                      value={formData.direccion}
                      onChange={handleChange}
                      className="input-field"
                      rows="2"
                      placeholder="Dirección completa del establecimiento"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'fiscal' && (
            <div className="max-w-3xl">
              <div className="space-y-8">
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary-600" />
                    Configuración de Moneda
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Moneda Principal</label>
                      <select
                        name="moneda"
                        value={formData.moneda}
                        onChange={handleChange}
                        className="input-field"
                      >
                        <option value="Q">Quetzal (GTQ)</option>
                        <option value="MXN">Peso Mexicano (MXN)</option>
                        <option value="USD">Dólar (USD)</option>
                        <option value="EUR">Euro (EUR)</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Símbolo (ej: Q, $, €)</label>
                      <input
                        type="text"
                        name="moneda_simbolo"
                        value={formData.moneda_simbolo}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
                  <div className="flex justify-between items-start mb-4">
                     <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                       <Check className="w-5 h-5" />
                       Impuestos (IVA / IGV)
                     </h3>
                     <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="usar_impuestos"
                          checked={formData.usar_impuestos} 
                          onChange={handleChange}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ml-3 text-sm font-medium text-blue-900">
                          {formData.usar_impuestos ? 'Activado' : 'Desactivado'}
                        </span>
                     </label>
                  </div>

                  <div className={`space-y-4 transition-all duration-300 ${formData.usar_impuestos ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <div>
                      <label className="label text-blue-900">Porcentaje de Impuesto (%)</label>
                      <div className="flex items-center gap-2 max-w-xs">
                        <input
                          type="number"
                          name="porcentaje_iva"
                          value={formData.porcentaje_iva}
                          onChange={handleChange}
                          className="input-field"
                          disabled={!formData.usar_impuestos}
                        />
                        <span className="text-blue-800 font-bold">%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                       <input
                         type="checkbox"
                         name="iva_incluido"
                         checked={formData.iva_incluido}
                         onChange={handleChange}
                         disabled={!formData.usar_impuestos}
                         className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                       />
                       <span className="text-sm text-blue-800">
                         Los precios de mis productos ya incluyen el impuesto
                       </span>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-xl">
                   <label className="label">Identificación Fiscal (RFC / NIT)</label>
                   <input
                      type="text"
                      name="rfc"
                      value={formData.rfc}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Ej: 123456-7"
                    />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'impresion' && (
            <div className="max-w-2xl space-y-6">
               <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Printer className="w-5 h-5" />
                    Formato de Ticket
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="label">Tipo de Impresora</label>
                      <div className="grid grid-cols-3 gap-3">
                        {['termica', 'matricial', 'laser'].map(type => (
                          <button
                            key={type}
                            onClick={() => {
                                setFormData(prev => ({...prev, impresora_tickets: type}));
                                setHasChanges(true);
                            }}
                            className={`p-3 rounded-lg border text-sm font-medium capitalize ${
                                formData.impresora_tickets === type 
                                ? 'bg-primary-50 border-primary-500 text-primary-700' 
                                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="label">Ancho del Papel</label>
                      <select
                        name="tamano_ticket"
                        value={formData.tamano_ticket}
                        onChange={handleChange}
                        className="input-field"
                      >
                        <option value="58mm">58mm (Estrecho)</option>
                        <option value="80mm">80mm (Estándar)</option>
                        <option value="A4">Carta / A4</option>
                      </select>
                    </div>
                  </div>
               </div>
            </div>
          )}

          {activeTab === 'sistema' && (
             <div className="max-w-2xl space-y-6">
               <div className="p-6 border rounded-xl">
                 <h3 className="font-semibold mb-4">Inventario y Alertas</h3>
                 
                 <div className="space-y-4">
                   <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Alertas de Stock Bajo</p>
                        <p className="text-xs text-gray-500">Avisar cuando un producto se esté agotando</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          name="notificaciones_stock"
                          checked={formData.notificaciones_stock} 
                          onChange={handleChange}
                          className="sr-only peer" 
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                   </div>

                   {formData.notificaciones_stock && (
                     <div className="pt-4 border-t">
                       <label className="label">Stock Mínimo Global</label>
                       <div className="flex items-center gap-3">
                         <input
                           type="number"
                           name="stock_minimo_alerta"
                           value={formData.stock_minimo_alerta}
                           onChange={handleChange}
                           className="input-field w-32"
                           min="1"
                         />
                         <span className="text-sm text-gray-500">unidades</span>
                       </div>
                       <p className="text-xs text-gray-400 mt-1">
                         Se usará este valor si el producto no tiene un mínimo específico.
                       </p>
                     </div>
                   )}
                 </div>
               </div>
             </div>
          )}
        </div>
      </div>

      {/* Sticky Footer for Saving */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 transition-transform duration-300 z-50 ${hasChanges ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4">
          <p className="text-gray-600 font-medium">
            Tienes cambios sin guardar
          </p>
          <button
            id="save-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 px-8 py-3 shadow-lg"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracionPage;
